import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, MessagesSquare, PlugZap, ShieldCheck, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, SurfaceTab, TabIcon, type SurfaceKey } from './CompanyBrain';
import { MoreThanAnswers } from './GigaForChatGPT2';
import { ConnectedAppsPillar } from '../components/alt/ConnectedAppsPillar';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';

// Order carries the argument: connect the tools, let what they know compound,
// take that intelligence to where people already work, and build on it.
const SECTIONS = [{
  id: 'connected-apps',
  label: 'Connected Apps',
  icon: PlugZap
}, {
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'workspace',
  label: 'Workspace',
  icon: MessagesSquare
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];
const HEADLINE_ROTATION_MS = 4200;
const HEADLINE_ROTATION_OFFSET_MS = 2100;
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('connected-apps');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [audienceIndex, setAudienceIndex] = useState(0);
  const navRef = useRef<HTMLDivElement | null>(null);
  const audiencePhrases = ['companies', 'founders', 'operators', 'teams'];
  useEffect(() => {
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      interval = window.setInterval(() => {
        setAudienceIndex(current => (current + 1) % audiencePhrases.length);
      }, HEADLINE_ROTATION_MS);
    }, HEADLINE_ROTATION_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [audiencePhrases.length]);
  // An IntersectionObserver band was mis-firing here: these sections are much
  // taller than the band, so with direction-dependent tie-breaking the active
  // tab could jump a section early or lag a section behind. A single reference
  // line just below the sticky nav is unambiguous — the active section is
  // simply the last one whose top has passed it.
  useEffect(() => {
    const update = () => {
      const line = (navRef.current?.getBoundingClientRect().bottom ?? 140) + 24;
      let current: SectionId = SECTIONS[0].id;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= line) current = section.id;
      }
      setActiveSection(current);
    };
    update();
    window.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const headerOffset = 76;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - headerOffset - 22;
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen pt-24 text-[#2B211A]" style={{
    backgroundColor: '#FAF8F4',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%232B211A' fill-opacity='0.10'/%3E%3C/svg%3E\")",
    backgroundSize: '8px 8px'
  }} data-id="element-5901" data-mp="1wv7k8c3ks29x">
      <section className="pb-10 pt-12 md:pb-14 md:pt-20" data-id="element-5902" data-mp="cdrou84ecibk">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5903" data-mp="cfcjryumi39y">
          <div className="mx-auto max-w-5xl text-center" data-id="element-5904" data-mp="2zkxkjx8sg7ly">
            <h1 className="font-title text-5xl font-bold leading-[0.95] tracking-tight text-[#2B211A] md:text-7xl" data-id="element-5905" data-mp="34k0r5m4f2lw1">
              The AI Platform for{' '}
              <span className="inline-flex overflow-hidden align-baseline" data-id="element-5906" data-mp="39hisaqc6zwth">
                <AnimatePresence mode="wait" initial={false} data-id="element-5907" data-mp="3djx811kkeggj">
                  <motion.span key={audiencePhrases[audienceIndex]} initial={{
                  y: '100%',
                  opacity: 0,
                  rotateX: -60
                }} animate={{
                  y: 0,
                  opacity: 1,
                  rotateX: 0
                }} exit={{
                  y: '-100%',
                  opacity: 0,
                  rotateX: 60
                }} transition={{
                  duration: 0.42,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-5908" data-mp="2gvhk19x3sf5j">
                    {audiencePhrases[audienceIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <br data-id="element-5909" data-mp="1pnfxti60r7z9" />
              that want to <HeadlineIcon icon={Zap} data-id="element-5910" data-mp="1poipvtf2vggr" />
              <UnderscoredPhrase data-id="element-5911" data-mp="1plcz89qsw6ix">Go Faster</UnderscoredPhrase>
              <br data-id="element-5912" data-mp="1lkt0x2uhqwv7" />
              and <HeadlineIcon icon={ShieldCheck} data-id="element-5913" data-mp="1lk18g0w6xf95" />
              <UnderscoredPhrase data-id="element-5914" data-mp="1gnarzcgh4hn7">Stay in Control</UnderscoredPhrase>
            </h1>
          </div>
        </div>
      </section>

      <div ref={navRef} className="sticky top-[72px] z-40" data-id="element-5915" data-mp="1bogcokhptb7u">
        <nav className="mx-auto grid max-w-[1280px] grid-cols-2 gap-2 px-3 py-4 sm:grid-cols-4 sm:gap-3 sm:px-6 md:flex md:justify-center md:px-10" aria-label="Section navigation" data-id="element-5916" data-mp="16x80jgp03bqz">
          {SECTIONS.map(section => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;
          return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full border px-2 py-2.5 text-[clamp(0.72rem,2.7vw,1rem)] font-semibold shadow-sm transition-all sm:gap-2 sm:px-4 md:shrink-0 md:px-5 md:text-lg ${isActive ? 'border-[#EA580C] bg-[#EA580C] text-white shadow-md hover:bg-[#C2410C]' : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#EA580C]/30 hover:text-[#2B211A]'}`} aria-current={isActive ? 'true' : undefined} data-id="element-5917" data-mp="sxgin4vd5b7p">
                <Icon size={19} className={`shrink-0 ${isActive ? 'text-white' : 'text-neutral-400'}`} data-id="element-5918" data-mp="9dhmp3fb1m3z" />
                <span className="min-w-0 leading-tight" data-id="element-5919" data-mp="x3bwtd88owkj">{section.label}</span>
              </button>;
        })}
        </nav>
      </div>

      <main data-id="element-5920" data-mp="nbprfxil1rpp">
        <section id="connected-apps" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5921" data-mp="na4uk18spwoo">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5922" data-mp="s8ojmaiorol9">
            <ConnectedAppsPillar data-id="element-5923" data-mp="1fzlj2ntaotfm" />
            <div className="mt-20 md:mt-28" data-id="element-5924" data-mp="1fyaisxz263t9">
              <ConnectedAppsTable data-id="element-5925" data-mp="1kqabq6gpnwgc" />
            </div>
          </div>
        </section>

        <section id="company-brain" className="scroll-mt-40 border-b border-neutral-200/70 bg-white py-20 md:py-28" data-id="element-5926" data-mp="35gz8232lgtqc">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5927" data-mp="30ie721fiq60u">
            <BrainFlowExperiment data-id="element-5928" data-mp="30frzw2wjhsj5" />
          </div>
        </section>

        <section id="co-worker" className="scroll-mt-40 border-b border-neutral-200/70 bg-[#FAF8F4] py-20 md:py-28" data-id="element-5929" data-mp="3f5h64cjhomqa">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5930" data-mp="3a8h7unsvso9q">
            <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-5931" data-mp="2mj65j1vvulth">
              <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-5932" data-mp="2hja2p5yx90fi">
                AI Co Worker
              </p>
              <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-5933" data-mp="2vkcnlfe6k4ou">
                No new tab for
                <br data-id="element-5934" data-mp="as13xahz0hhm" />
                your team to open.
              </h2>
              <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-5935" data-mp="aueyli8qnron">
                Everything your company builds in Giga can show up as a co
                worker in Slack, ChatGPT, and Claude Code — with Teams and
                iMessage next. Same connections, same brain, no new tab.
              </p>
            </div>

            {/* One centred column: the surface switcher sits directly above the
             frame it controls, so the relationship needs no explaining. */}
            <div className="mx-auto max-w-[820px]" data-id="element-5936" data-mp="2j0fpx88ty399">
              {/* Deliberately quieter and smaller than the four pillar tabs at
               the top of the page — this is a local control, not navigation —
               but the platform logos stay so each one is identifiable. */}
              <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden justify-center md:mb-10 md:flex" tabClassName="!px-3 !py-1.5 !text-[13px] !gap-1.5 !shadow-none !ring-0" data-id="element-5937" data-mp="2swimzzotrbpc" />
              <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-5938" data-mp="13incom48zv34" />
            </div>
          </div>
        </section>

        <section id="workspace" className="scroll-mt-40 bg-white py-20 md:py-28" data-id="element-5939" data-mp="3guzyvf1vg1pt">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5940" data-mp="3c1f7i5usa5zl">
            <PlatformPillar data-id="element-5941" data-mp="380lif95lul54" />
          </div>
        </section>

        <section className="bg-[#FAF8F4] py-20 md:py-28" data-id="element-5942" data-mp="37z0lxdzqcs4m">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-5943" data-mp="333laesp738bd">
            <AccelerationControlSection data-id="element-5944" data-mp="3nop6ybrdtyc" />
            <ClosingCTA data-id="element-5945" data-mp="3mn2txpall1b" />
          </div>
        </section>
      </main>
    </div>;
}
// Sized in em so the tile tracks the headline as it scales across breakpoints.
function HeadlineIcon({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  return <span aria-hidden="true" className="mr-[0.18em] inline-flex size-[0.86em] -translate-y-[0.06em] items-center justify-center rounded-[0.2em] bg-[#EA580C] align-middle" data-id="element-5946" data-mp="3lsrxst0xoo3i">
      <Icon className="size-[0.56em] text-white" strokeWidth={2.6} data-id="element-5947" data-mp="2famoazcjzxtl" />
    </span>;
}
function UnderscoredPhrase({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="relative inline-block align-baseline" data-id="element-5948" data-mp="20vqclfpnm5ge">
      <span className="absolute inset-x-0 bottom-[-4px] h-[9px] bg-[#EA580C]" aria-hidden="true" data-id="element-5949" data-mp="1vvlslfpjeslf" />
      <span className="relative z-10" data-id="element-5950" data-mp="2aki3dkizajpq">{children}</span>
    </span>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-5951" data-mp="2jk55k8jj7tju" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5952" data-mp="2th01qksc1huy">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5953" data-mp="z4tcwj6in2k6" />
        {mobileTabs}
        <div className="relative rounded-[22px] rounded-tr-none bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:rounded-[24px]" data-id="element-5954" data-mp="u7jq7baih501">
          <MoreThanAnswers embedded data-id="element-5955" data-mp="3asor55skrv29" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5956" data-mp="2w4222cd5boxu">
        {mobileTabs}
        <CodeTerminal data-id="element-5957" data-mp="311am6ihuu31q" />
      </div>;
  }
  // Width matches SlackPanel's own max-width so the orange halo hugs the frame
  // instead of showing a strip of background down each side.
  return <div className="relative mx-auto w-full max-w-[720px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-5958" data-mp="2r5qpvuzdp2cg">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-5959" data-mp="2d6r2eqnlqthv" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-5960" data-mp="bdjq7q3acs07">
        <SlackPanel className="w-full" heightClass="h-[520px] sm:h-[600px]" data-id="element-5961" data-mp="bbqevxhq9jlv" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5962" data-mp="17qli6p39l" />
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-5963" data-mp="so6tnbkz4yb0" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5964" data-mp="ntsvj1exgd2n" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5965" data-mp="1gc6lyfxxowrp">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-5966" data-mp="1junht3qjc4eo">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  return <div className={`w-full items-center gap-2.5 md:gap-3 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-5967" data-mp="26utpos83087s">
      <SurfaceTab surface="slack" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-5968" data-mp="2bl1rsovfgwl2">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-5969" data-mp="2gjbmyzh5jx6o" />
        Slack
      </SurfaceTab>
      <SurfaceTab surface="code" activeSurface={activeSurface} setActiveSurface={setActiveSurface} ariaLabel="Claude" className={tabClassName} data-id="element-5970" data-mp="2lhn0bk6noptn">
        <TabIcon slug="claude" fallback="C" data-id="element-5971" data-mp="3n9fqu0alu43n" />
        Claude
      </SurfaceTab>
      <SurfaceTab surface="chatgpt" activeSurface={activeSurface} setActiveSurface={setActiveSurface} className={tabClassName} data-id="element-5972" data-mp="3ibe7rfd6i4s6">
        <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-5973" data-mp="tlbscnhdxaa" />
        ChatGPT
      </SurfaceTab>
    </div>;
}
const ACCELERATION_CONTROL_GROUPS = [{
  heading: 'Go Faster',
  items: [{
    title: 'Offload busywork and associate work',
    copy: 'Hand Giga the repetitive research, formatting, follow-up, and first-draft work so your team can spend more time on judgment calls.'
  }, {
    title: 'Kill Meetings',
    copy: 'Turn scattered conversations into crisp updates, decisions, and next steps without adding another recurring sync to the calendar.'
  }, {
    title: 'Stop waiting for numbers',
    copy: 'Pull live metrics from the systems you already use and get answers, reports, and analysis without waiting on manual exports.'
  }]
}, {
  heading: 'Stay In Control',
  items: [{
    title: 'Own your own data',
    copy: 'Export your data, keep your history portable, and switch systems whenever you need without losing the work your team has built.'
  }, {
    title: 'Get Real Time Visibility',
    copy: 'See what is connected, what is being used, and where sensitive work is happening across your team as it happens.'
  }, {
    title: 'Built for teams',
    copy: 'Manage shared access, team workflows, and company context from one place instead of stitching together personal AI accounts.'
  }]
}] as const;
function AccelerationControlSection() {
  return <div data-id="element-5974" data-mp="153vg30ylt3ey">
      {ACCELERATION_CONTROL_GROUPS.map((group, groupIndex) => <section key={group.heading} className={groupIndex > 0 ? 'mt-16 md:mt-20' : undefined} data-id="element-5975" data-mp="1a2zr7x539b7x" data-mpi={groupIndex}>
          <h2 className="font-title text-4xl font-bold leading-tight text-[#2B211A] md:text-6xl" data-id="element-5976" data-mp="1xsk7g89uxudi">
            {group.heading}
          </h2>
          <div className="mt-8 border-y border-neutral-200" data-id="element-5977" data-mp="22ldg378g5muv">
            {group.items.map((item, index) => <motion.article key={item.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.42,
          delay: index * 0.03,
          ease: [0.22, 1, 0.36, 1]
        }} className="grid gap-4 border-b border-neutral-200 py-8 last:border-b-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-8 md:py-10" data-id="element-5978" data-mp="2cf96ya4407jw" data-mpi={index}>
                <h3 className="font-title text-2xl font-bold leading-tight text-[#2B211A] md:text-3xl" data-id="element-5979" data-mp="1oovjyy0dsyw6">{item.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#6B5D52] md:text-lg md:leading-8" data-id="element-5980" data-mp="1fpic29rwoidk">{item.copy}</p>
              </motion.article>)}
          </div>
        </section>)}
    </div>;
}
function ClosingCTA() {
  return <div className="mt-20 overflow-hidden rounded-[28px] bg-[#2B211A] px-8 py-14 text-center md:mt-28 md:px-16 md:py-20" data-id="element-5981" data-mp="15rlzren8fhdv">
      <h2 className="mx-auto max-w-[24ch] font-title text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-[52px]" data-id="element-5982" data-mp="3hdmk3xg1l7fq">
        One platform to run your company's AI on.
      </h2>
      <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-white/65" data-id="element-5983" data-mp="3rclw9q00nl7h">
        Connect your tools, build your workflows, put a co worker where your
        team already is, and let the brain get sharper every week.
      </p>
      <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-5984" data-mp="2yq126hs5ov08">
        <Link to="/signup" className="inline-flex items-center gap-2 rounded-full bg-[#EA580C] px-7 py-3.5 text-base font-bold text-white transition-colors hover:bg-[#C2410C]" data-id="element-5985" data-mp="33nssj7d5k92b">
          Get started for free
          <ArrowRight size={17} data-id="element-5986" data-mp="3cgw57dfz4hvw" />
        </Link>
        <Link to="/demo/book" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-base font-bold text-white transition-colors hover:border-white/60" data-id="element-5987" data-mp="2fy8coxcaprx9">
          Book a demo
        </Link>
      </div>
      <p className="mt-6 text-[13.5px] text-white/45" data-id="element-5988" data-mp="1dchpurnjzpmi">
        $100 in credits included · no card needed
      </p>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  anthropic: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  anthropic: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  anthropic: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  anthropic: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  anthropic: null,
  giga: 'Coming soon'
}];
function ConnectedAppsTable() {
  return <div className="mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_80px_-48px_rgba(43,33,26,0.4)]" data-id="element-5989" data-mp="2jr5cchsvm8p8">
      <div className="overflow-x-auto" data-id="element-5990" data-mp="2a1lqr4ds40qo">
        <table className="w-full min-w-[660px] border-collapse text-left" data-id="element-5991" data-mp="2a0ke4vh4766u">
          <thead data-id="element-5992" data-mp="2xqoij524n9yw">
            <tr className="border-b border-neutral-200" data-id="element-5993" data-mp="2xq543ybor3pm">
              <th scope="col" className="w-[48%] px-5 py-4 md:px-6" data-id="element-5994" data-mp="32iy7oivf4604">
                <span className="sr-only" data-id="element-5995" data-mp="32k9c8kwothsa">Feature</span>
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-5996" data-mp="r1mkwjqniol7">
                ChatGPT
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-5997" data-mp="zw0ymhvj9r27">
                Claude
              </th>
              <th scope="col" className="w-[18%] bg-[#FFF8F3] px-4 py-4 font-title text-[15px] font-bold text-[#2B211A] md:text-base" data-id="element-5998" data-mp="84lecuhg8gf5">
                Giga
              </th>
            </tr>
          </thead>
          <tbody data-id="element-5999" data-mp="h51f3w6t59gb">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className="border-b border-neutral-100 last:border-b-0" data-id="element-6000" data-mp="h6unph0ze3qy">
                <th scope="row" className="px-5 py-3.5 text-[14px] font-semibold leading-snug text-[#2B211A] md:px-6" data-id="element-6001" data-mp="c5eydcrrtb2r">
                  <span className="block" data-id="element-6002" data-mp="1silrjmrnqb7v">{row.feature}</span>
                  <span className="mt-1 block max-w-xl text-[12.5px] font-normal leading-[1.45] text-[#8A7B70]" data-id="element-6003" data-mp="1nkbsv26q4shi">
                    {row.description}
                  </span>
                </th>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6004" data-mp="q68wznz53rmn">
                  <ComparisonValue value={row.chatgpt} data-id="element-6005" data-mp="u8m52j1d01ml" />
                </td>
                <td className="px-4 py-3.5 text-[#2B211A]" data-id="element-6006" data-mp="u9f5l29q5uwa">
                  <ComparisonValue value={row.anthropic} data-id="element-6007" data-mp="z6nmkzj4zsvy" />
                </td>
                <td className="bg-[#FFF8F3] px-4 py-3.5 text-[#2B211A]" data-id="element-6008" data-mp="2hzjd9c2ya6l">
                  <ComparisonValue value={row.giga} highlight data-id="element-6009" data-mp="2fms25kfmngv" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function ComparisonValue({
  value,
  highlight = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
}) {
  if (value === true) {
    return <span className={`inline-flex items-center ${highlight ? 'text-[#EA580C]' : 'text-[#2B211A]'}`} aria-label="Supported" data-id="element-6010" data-mp="3eyuq5ficondh">
        <Check size={19} strokeWidth={2.6} aria-hidden="true" data-id="element-6011" data-mp="3jx5z7o0tmtun" />
      </span>;
  }
  if (value === false) {
    return <span className="inline-flex items-center text-neutral-300" aria-label="Not supported" data-id="element-6012" data-mp="3cddh94ok1j48">
        <X size={19} strokeWidth={2.4} aria-hidden="true" data-id="element-6013" data-mp="37f252nasewiu" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className="text-neutral-300" data-id="element-6014" data-mp="32f7k10dp115p">—</span>;
  }
  return <span className={`text-[12px] font-semibold leading-tight ${highlight ? 'text-[#EA580C]' : 'text-[#6B5D52]'}`} data-id="element-6015" data-mp="2yff585xphyrw">{value}</span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-6016" data-mp="2on9hx9ieg5j1">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-6017" data-mp="2jpho29y729c7">
        <Icon size={22} strokeWidth={2.4} data-id="element-6018" data-mp="2eqoayimtkkck" />
      </span>
      {label}
    </p>;
}